/**
 * PairUpToday — Standard Indian Phone Normalization & Validation Utility
 *
 * Authoritatively validates and normalizes Indian mobile numbers across the platform:
 * - 10 digits beginning with 6–9: e.g. "9830012345"
 * - 11 digits beginning with 0 followed by 6–9: e.g. "09830012345"
 * - 12 digits beginning with 91 followed by 6–9: e.g. "+91 98300 12345" or "919830012345"
 * - 14 digits beginning with 0091 followed by 6–9: e.g. "00919830012345"
 *
 * Strict International Guard:
 * Non-Indian numbers (e.g. UK +44, US +1, UAE +971, etc.) and malformed digits are strictly rejected
 * to prevent international identity collisions and SMS delivery failures.
 */

export function normalizeIndianPhone(phone: unknown): string | null {
  if (typeof phone !== 'string' && typeof phone !== 'number') {
    return null;
  }

  const raw = String(phone).trim();
  if (!raw) return null;

  // Strip all non-digit characters
  const digits = raw.replace(/\D/g, '');

  // 1. Exactly 10 digits starting with 6-9
  if (/^[6-9]\d{9}$/.test(digits)) {
    return digits;
  }

  // 2. 11 digits starting with 0 followed by 6-9
  if (/^0[6-9]\d{9}$/.test(digits)) {
    return digits.slice(1);
  }

  // 3. 12 digits starting with 91 followed by 6-9
  if (/^91[6-9]\d{9}$/.test(digits)) {
    return digits.slice(2);
  }

  // 4. 14 digits starting with 0091 followed by 6-9
  if (/^0091[6-9]\d{9}$/.test(digits)) {
    return digits.slice(4);
  }

  // All other lengths, international country codes, or invalid starting digits are rejected
  return null;
}

export function isValidIndianPhone(phone: unknown): boolean {
  return normalizeIndianPhone(phone) !== null;
}
